import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, useTheme } from '@mui/material';
import { ChatBubbleOutlineOutlined, ZoomOutMapOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { GET_COMMENTS_QUERY } from 'features/offer/edit/sidebar/queries';
import DocumentCommentComponent from './DocumentCommentComponent';
import AddCommentContainer from './AddCommentContainer';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useMutation } from '@apollo/client';
import { DELETE_COMMENT_MUTATION } from '../../mutations';
import { DOCUMENT_COMMENT_DELETED } from '../../subscriptions';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { defaultComment } from '../../constants/defaultComment';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { focusCommentKeyframes, focusCommentTiming } from './animation';
import { set, useStateLens } from '@totalsoft/react-state-lens';
import {
  CommentDocumentStateLabel,
  CommentsContentWrapper,
  CommentsDateDivider,
  CommentsFooterWrapper,
  CommentsHeaderWrapper
} from './CommentsStyles';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { getInt } from 'utils/functions';

function DocumentCommentsContainer({ documentId, siteId, history, fullScreen, dirtyComment, setDirtyComment }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const location = useLocation();

  const [comment, setComment] = useState(defaultComment);
  const [documentCommentsLens, , resetDocumentCommentsLens] = useChangeTrackingLens([]);
  const documentComments = documentCommentsLens |> get;
  const contentEndRef = useRef();
  const currentURL = useRef('');
  const canSwitch = useStateLens(false);
  const documentExists = getInt(documentId) != null && getInt(siteId) != null;
  const userInfo = useSelector(state => state.userInfo, shallowEqual);

  const { loading, refetch: refetchComments } = useQueryWithErrorHandling(GET_COMMENTS_QUERY, {
    variables: { documentId, siteId },
    cacheEviction: true,
    skip: !documentExists,
    onCompleted: data => {
      const comments = data.getComments.list.map(x => {
        return { ...x, avatar: data.getComments.usersAdditionalData.find(y => y.userId === x.userId)?.avatar };
      });
      resetDocumentCommentsLens(comments);
    }
  });

  useEffect(() => {
    currentURL.current = window.location.pathname;
  }, []);

  useEffect(() => {
    const unblock = history.block(nextLocation => {
      const isActionOutsideTheCurrentDocument =
      nextLocation.pathname.split('/')[1] !== currentURL?.current?.split('/')[1] ||
      nextLocation.pathname.split('/')[2] !== currentURL?.current?.split('/')[2];

      if (dirtyComment && isActionOutsideTheCurrentDocument) {
        const confirmMessage = t('LeavingWithoutSavingComment');
        if (window.confirm(confirmMessage)) {
          currentURL.current = nextLocation.pathname;
          return true;
        }
        return false;
      }
      currentURL.current = nextLocation.pathname;
      return true;
    });

    return () => unblock();
  }, [location, history, dirtyComment, t]);

  const getParentComment = parentCommentId => documentComments.find(c => c.commentId === parentCommentId);

  useGlobalSubscription(DOCUMENT_COMMENT_DELETED, {
    variables: {
      documentId,
      siteId,
      userPartnerId: userInfo.userExtraInfoModel.userPartnerId
    },
    skip: !documentExists
  });

  const [deleteComment] = useMutation(DELETE_COMMENT_MUTATION, {
    onError: error => {
      showError(error.message);
    }
  });

  const handleDeleteComment = useCallback(
    async commentId => {
      await deleteComment({ variables: { commentId } });
    },
    [deleteComment]
  );

  const handleReplyComment = commentId => {
    const parentComment = getParentComment(commentId);
    const validRecipients = [];
    if (parentComment.userEmail) {
      validRecipients.push({ email: parentComment.userEmail, contactName: null, isStandardContact: true, userId: parentComment.userId });
    }
    (canSwitch |> set)(true);
    setDirtyComment(true);
    setComment({
      ...defaultComment,
      parentCommentId: commentId,
      parentCommentText: parentComment.commentText,
      parentComment: parentComment,
      internal: parentComment.internal,
      recipients: validRecipients
    });
  };

  const handleEditComment = commentId => {
    let currentComment = getParentComment(commentId);
    let parentComment = getParentComment(currentComment.parentCommentId);
    (canSwitch |> set)(true);
    setComment({
      ...currentComment,
      parentComment: parentComment
        ? {
            ...parentComment,
            commentText: currentComment.parentCommentText
          }
        : parentComment,
      internal: parentComment?.internal || currentComment?.internal || false
    });
    setDirtyComment(true);
  };

  useEffect(() => {
    if (documentComments.length) contentEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }, [documentComments.length]);

  const handleRedirectToFullWith = () => {
    if (pathname.includes('offers')) history.push(`/offers/${documentId}/${siteId}/${fullScreen ? 'assets' : 'comments'}`);
    else if (pathname.includes('folders')) history.push(`/folders/${documentId}/${siteId}/${fullScreen ? 'generalData' : 'comments'}`);
    else if (pathname.includes('facilities'))
      history.push(`/facilities/${documentId}/${siteId}/${fullScreen ? 'generalData' : 'comments'}`);
    else if (pathname.includes('partner-files')) history.push(`/partner-files/${documentId}/${siteId}${fullScreen ? '' : '/comments'}`);
  };

  const parentCommentsRefs = useRef([]);
  parentCommentsRefs.current = [];

  const addToRefs = (ref, commentId) => {
    const isParentComment = documentComments.some(comment => comment.parentCommentId === commentId);
    if (ref && !parentCommentsRefs.current.includes(ref) && isParentComment) parentCommentsRefs.current.push(ref);
  };

  const handleClickToScroll = parentCommentId => {
    const parentCommentRef = parentCommentsRefs.current.find(el => el.id === `comment_${parentCommentId}`);
    if (!parentCommentRef) return;
    parentCommentRef.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    parentCommentRef.animate(focusCommentKeyframes(theme), focusCommentTiming);
  };

  const inputRef = useRef();

  return (
    <Grid container flexDirection='column' height={fullScreen ? '75vh' : '100vh'} flexWrap='nowrap'>
      <CommentsHeaderWrapper container justifyContent='space-between' alignItems='center'>
        <Grid item display='flex' alignItems='center' gap={1}>
          <ChatBubbleOutlineOutlined />
          <Typography fontWeight='bold' textTransform='uppercase' variant='subtitle1'>
            {t('Offer.Comments.Title')}
          </Typography>
        </Grid>
        <Grid item display='flex' alignItems='center' gap={1}>
          <IconButton variant='text' size='small' onClick={handleRedirectToFullWith} disabled={dirtyComment}>
            {fullScreen && <OpenInNewOutlined />}
            {!fullScreen && <ZoomOutMapOutlined />}
          </IconButton>
        </Grid>
      </CommentsHeaderWrapper>
      <CommentsContentWrapper item flexGrow={1} px={2} pb={2} overflow='auto'>
        <Grid container>
          {loading && <WrappedFakeText lines={5} py={2} />}
          {!loading &&
            documentComments.map((comment, index) => {
              return (
                <>
                  {(index === 0 ||
                    new Date(comment.date).toDateString() !== new Date(documentComments[index - 1]?.date)?.toDateString()) && (
                    <Grid item xs={12} mt={1.25}>
                      <CommentsDateDivider>{t('DATE', { date: comment.date })}</CommentsDateDivider>
                    </Grid>
                  )}
                  {(index === 0 || comment.documentStateId !== documentComments[index - 1]?.documentStateId) && (
                    <Grid item container xs={12} justifyContent='center' mt={2}>
                      <CommentDocumentStateLabel>{comment.documentStateName}</CommentDocumentStateLabel>
                    </Grid>
                  )}
                  <Grid item xs={12} mt={2}>
                    <DocumentCommentComponent
                      comment={comment}
                      parentComment={getParentComment(comment.parentCommentId)}
                      onReplyComment={handleReplyComment}
                      onEditComment={handleEditComment}
                      onDeleteComment={handleDeleteComment}
                      addToRefs={addToRefs}
                      handleClickToScroll={handleClickToScroll}
                    />
                  </Grid>
                </>
              );
            })}
          <div ref={contentEndRef} />
        </Grid>
      </CommentsContentWrapper>
      {documentExists && (
        <CommentsFooterWrapper item>
          <AddCommentContainer
            documentId={documentId}
            siteId={siteId}
            comment={comment}
            setComment={setComment}
            fullScreen={fullScreen}
            canSwitch={canSwitch}
            refetchComments={refetchComments}
            inputRef={inputRef}
            dirtyComment={dirtyComment}
            setDirtyComment={setDirtyComment}
          />
        </CommentsFooterWrapper>
      )}
    </Grid>
  );
}

DocumentCommentsContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  history: PropTypes.object.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  dirtyComment: PropTypes.bool.isRequired,
  setDirtyComment: PropTypes.func.isRequired
};

export default DocumentCommentsContainer;
